/**
 * polylinePlane — the PLANE an OCS-planar polyline's 2D construction happens
 * in, and the exact round trip between it and the scene's WCS.
 *
 * # The defect this exists to close (@codex PR #785 round 5, FINDING B)
 *
 * A 2D heavy `POLYLINE` stores its vertices as OBJECT coordinates in the plane
 * of its group-210 extrusion (Autodesk DXF reference, "OCS and Extrusion
 * Direction"). `cad-core`'s `heavyPolylineWcsResolver` lifts them to WCS, and
 * the typed adapter hands those lifted points to the scene — correct for the
 * CENTRELINE, which is just the points.
 *
 * A WIDTH BAND is not just the points. It is built by offsetting each span
 * TRANSVERSELY by half its width, and "transverse" is a direction IN THE
 * ENTITY'S PLANE. `polylineWidthOutline.ts` offsets in XY (its left normal is
 * `(-dy, dx)`), which is the plane's transverse direction only when the plane
 * IS the WCS XY plane. Offsetting the already-lifted points therefore drew a
 * band in the wrong plane: for extrusion `(0, 1, 0)` the OCS y axis maps to
 * WCS **Z**, so the band should be edge-on (collapsed) in the top view, while
 * the XY offset drew a full-width ribbon lying flat. The OCS lift is not
 * width-preserving, so the offset cannot be applied after it.
 *
 * The vector-PDF plot never had the bug — `widePolylineBand.ts` builds the
 * band from the RAW OCS x/y and maps the finished loops through the affine
 * that carries the lift (`heavyPolylineConverter.ts::ocsMatrix`), exactly as
 * `ezdxf`'s `addons/drawing/frontend.py` builds its `TraceBuilder` in the
 * entity's OCS and lifts the finished polygons. So screen and paper disagreed,
 * and the canvas was the side that was wrong.
 *
 * # What this module provides
 *
 * The same "construct in the plane, then lift" order for the CANVAS, which
 * receives points that are ALREADY lifted: a plane is a pair of exact inverse
 * maps, so the band pipeline projects the lifted points back into the plane's
 * own 2D coordinates, runs the unchanged 2D construction there, and lifts the
 * finished loops. Composed with the adapter's lift, that is the same
 * "offset in OCS, then lift" the plot performs.
 *
 * The basis is `drawing-model`'s `computeOcsAxes` — the ONE Arbitrary Axis
 * Algorithm implementation, the same one the lift itself used, so the
 * projection cannot land in a differently-seeded basis than the lift did. It
 * is ORTHONORMAL, so the inverse is the transpose and the round trip is exact
 * to floating point; no matrix inversion and no conditioning question.
 *
 * A DEFAULT / absent extrusion returns `null` rather than the identity pair,
 * so the overwhelmingly common planar polyline runs the byte-identical code
 * path it always did — no projection, no re-composition, not even a signed
 * zero normalised.
 *
 * # Not a second OCS truth
 *
 * Nothing here decides WHICH plane an entity is in: the extrusion arrives on
 * the scene geometry, put there by the adapter that read it off the entity.
 * This module only changes coordinates within that decision, which is why it
 * is a renderer-side geometry helper and not new CAD semantics.
 *
 * # The same seam, one round later (@codex PR #785 round 6)
 *
 * Round 5 closed the OCS half of "a width is not a scalar under a general map,
 * so build the band where the width is meaningful and then map it". The INSERT
 * half is the same sentence: a block-child polyline under a NON-SIMILARITY
 * INSERT (`|scaleX| !== |scaleY|`) had its authored width reduced to
 * `width · |scaleX|` and the band offset in world, so a horizontal span of
 * width 2 under `S(2, 3)` drew width 4 where the file says 6 — and the plot,
 * which builds the band from the RAW block-local vertices and maps the
 * finished loops through the block affine, drew 6. Hence {@link
 * linearPolylinePlane}: the very same "pair of exact inverse maps" shape, for
 * a map that is no longer orthonormal. `widePolylineBandLoops` takes either,
 * so there is ONE place that decides the order for both halves.
 */

import {
  computeOcsAxes,
  cross,
  dot,
  isDefaultExtrusion,
  length as vec3Length,
  ocsToWcs,
  type Vec3,
} from "@dxf-ai-editor/drawing-model";

/**
 * A GENERAL linear frame: the images of a band-authoring frame's own unit
 * axes, as three WCS columns. Today it is the 2x2-plus-Z affine a block INSERT
 * applies on top of a child polyline's own OCS plane, which is why it is not
 * required to be orthonormal (a NON-SIMILARITY insert is exactly the case a
 * rigid plane cannot express). {@link linearPolylinePlane} inverts it.
 *
 * Defined HERE rather than in the renderer's `scene3d/types.ts` (which
 * re-exports it, so no import path changed) because the two consumers of the
 * band pipeline now sit on opposite sides of the package boundary — the canvas
 * batcher / vector plot in `apps/web`, and `cad-core`'s own heavy-POLYLINE
 * SELECT geometry — and `cad-core` may not import `apps/*`.
 */
export interface PolylineBandFrame {
  ax: Vec3;
  ay: Vec3;
  az: Vec3;
}

/**
 * A pair of exact inverse maps between the scene's WCS and one OCS plane's own
 * coordinates, in which the plane is the `z = const` plane and the 2D band /
 * arc construction is meaningful.
 */
export interface PolylinePlane {
  /** WCS → plane coordinates (`x`/`y` in-plane, `z` along the extrusion). */
  toLocal(p: Vec3): Vec3;
  /** Plane coordinates → WCS. The exact inverse of {@link toLocal}. */
  toWorld(p: Vec3): Vec3;
}

/**
 * The plane of an OCS-planar polyline, or `null` when its extrusion is the
 * DXF default (0,0,1) — in which case the plane IS the WCS XY plane and every
 * caller must skip the change of coordinates entirely (see the module doc).
 */
export function polylinePlane(extrusion?: Vec3): PolylinePlane | null {
  if (isDefaultExtrusion(extrusion)) return null;
  const axes = computeOcsAxes(extrusion);
  const { ax, ay, az } = axes;
  return {
    // Orthonormal basis ⇒ the inverse of the lift is its TRANSPOSE, i.e. the
    // component of the point along each axis. (`ocsToWcs` is the forward map,
    // shared with the lift itself.)
    toLocal: (p) => ({
      x: p.x * ax.x + p.y * ax.y + p.z * ax.z,
      y: p.x * ay.x + p.y * ay.y + p.z * ay.z,
      z: p.x * az.x + p.y * az.y + p.z * az.z,
    }),
    toWorld: (p) => ocsToWcs(p, axes),
  };
}

/**
 * The plane pair for a GENERAL linear frame — the columns `ax`/`ay`/`az` are
 * the images of the frame's own unit axes, so `toWorld` is the matrix they
 * form and `toLocal` is its inverse (Cramer / adjugate: the rows of the
 * inverse are the cross products of the other two columns over the
 * determinant).
 *
 * Unlike {@link polylinePlane} the basis is NOT orthonormal, so the inverse is
 * a real inverse rather than the transpose — which is exactly the point: a
 * non-similarity INSERT is what makes a width stop being a scalar, and an
 * orthonormal frame could not express it.
 *
 * Returns `null` for a SINGULAR (or non-finite) frame — a zero scale factor on
 * any axis — rather than emitting infinities: the caller then falls back to the
 * plain path, which draws the same degenerate nothing without poisoning the
 * vertex buffer with `NaN`. The test is RELATIVE to the columns' own
 * magnitudes, so a legitimately tiny-but-uniform block scale is not mistaken
 * for a collapse.
 */
export function linearPolylinePlane(
  frame: PolylineBandFrame,
): PolylinePlane | null {
  const { ax, ay, az } = frame;
  const r0 = cross(ay, az);
  const det = dot(ax, r0);
  const scale = vec3Length(ax) * vec3Length(ay) * vec3Length(az);
  if (!Number.isFinite(det) || !Number.isFinite(scale)) return null;
  if (!(Math.abs(det) > 1e-12 * scale) || scale === 0) return null;
  const r1 = cross(az, ax);
  const r2 = cross(ax, ay);
  return {
    toLocal: (p) => ({
      x: dot(r0, p) / det,
      y: dot(r1, p) / det,
      z: dot(r2, p) / det,
    }),
    toWorld: (p) => ({
      x: p.x * ax.x + p.y * ay.x + p.z * az.x,
      y: p.x * ax.y + p.y * ay.y + p.z * az.y,
      z: p.x * ax.z + p.y * ay.z + p.z * az.z,
    }),
  };
}
